import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TablePagination,
  useTheme,
  useMediaQuery,
  Stack,
  Card,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { usePermission } from "../hooks/usePermission";
import config from "../config/config";
import { useToast } from "../context/ToastContext";

const Categories = () => {
  const { showToast } = useToast();
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCategories, setTotalCategories] = useState(0);
  const [open, setOpen] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [form, setForm] = useState({
    name: "",
    description: "",
  });
  const [validationErrors, setValidationErrors] = useState({});
  const { token } = useAuth();
  const { hasPermission } = usePermission();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, page, rowsPerPage]);

  const fetchCategories = async () => {
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
      };
      if (searchQuery) params.search = searchQuery;

      const res = await axios.get(`${config.backendUrl}/api/categories`, {
        headers: { "x-auth-token": token },
        params,
      });
      setCategories(res.data.docs || []);
      setTotalCategories(res.data.totalDocs || 0);
    } catch (err) {
      console.error(err);
      setCategories([]);
      setTotalCategories(0);
    }
  };

  const handleClickOpen = (category = null) => {
    setCurrentCategory(category);
    if (category) {
      setForm({
        name: category.name,
        description: category.description,
      });
    } else {
      setForm({
        name: "",
        description: "",
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setCurrentCategory(null);
    setForm({
      name: "",
      description: "",
    });
    setValidationErrors({});
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!form.name) errors.name = "Tên danh mục là bắt buộc";

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      if (currentCategory) {
        await axios.put(
          `${config.backendUrl}/api/categories/${currentCategory._id}`,
          form,
          {
            headers: { "x-auth-token": token },
          }
        );
        showToast("Danh mục đã được cập nhật thành công!", "success");
      } else {
        await axios.post(`${config.backendUrl}/api/categories`, form, {
          headers: { "x-auth-token": token },
        });
        showToast("Danh mục đã được thêm thành công!", "success");
      }
      fetchCategories();
      handleClose();
    } catch (err) {
      console.error(err);
      let errorMessage = "Đã xảy ra lỗi khi lưu danh mục.";
      if (err.response && err.response.data) {
        if (err.response.data.errors) {
          const apiErrors = {};
          err.response.data.errors.forEach((error) => {
            apiErrors[error.param] = error.msg;
          });
          setValidationErrors(apiErrors);
          errorMessage = err.response.data.errors[0].msg || errorMessage;
        } else if (err.response.data.message) {
          errorMessage = err.response.data.message;
        }
      }
      showToast(errorMessage, "error");
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${config.backendUrl}/api/categories/${id}`, {
        headers: { "x-auth-token": token },
      });
      fetchCategories();
      showToast("Danh mục đã được xóa thành công!", "success");
    } catch (err) {
      console.error(err);
      let errorMessage = "Đã xảy ra lỗi khi xóa danh mục.";
      if (err.response && err.response.data && err.response.data.message) {
        errorMessage = err.response.data.message;
      }
      showToast(errorMessage, "error");
    }
  };

  return (
      <Container maxWidth="lg">
        <Box sx={{ px: { xs: -20, sm: 3, md: 4 }, py: 3 }}>
          <Typography
            variant={isMobile ? "h6" : "h4"}
            gutterBottom
            sx={{ fontWeight: 700, mb: 4, mt: isMobile ? -4 : 4 }}
          >
          Quản lý Danh mục 
        </Typography>
        {totalCategories} Danh mục 
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 2,
            mb: 2,
            position: isMobile ? "sticky" : "static",
            top: isMobile ? "56px" : "auto",
            zIndex: 10,
            backgroundColor: "background.paper",
            p: 0,
            borderBottom: isMobile ? "1px solid #ddd" : "none",
          }}
        >
          <TextField
            label="Tìm kiếm theo tên"
            variant="outlined"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flexGrow: 1 }}
          />
        </Box>
        {hasPermission(["category:create"]) && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleClickOpen()}
          >
            Thêm Danh mục
          </Button>
        )}

        {isMobile ? (
          <Stack spacing={2} sx={{ mt: 2 }}>
            {categories.length > 0 ? (
              categories.map((category) => (
                <Card
                  key={category._id}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    boxShadow: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <Typography fontWeight={600}>{category.name}</Typography>
                  <Typography variant="body2">
                    {category.description || "Không có mô tả"}
                  </Typography>
                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}
                  >
                    {hasPermission(["category:update"]) && (
                      <IconButton
                        onClick={() => handleClickOpen(category)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                    {hasPermission(["category:delete"]) && (
                      <IconButton
                        onClick={() => handleDelete(category._id)}
                        color="secondary"
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                </Card>
              ))
            ) : (
              <Typography align="center" color="text.secondary">
                Không có danh mục nào.
              </Typography>
            )}
            {isMobile && (page + 1) * rowsPerPage < totalCategories && (
              <Box sx={{ mt: 2, textAlign: "center" }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setPage((prev) => prev + 1);
                  }}
                >
                  Tải thêm danh mục
                </Button>
              </Box>
            )}
          </Stack>
        ) : (
          <>
            <TableContainer component={Paper} sx={{ mt: 4 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Tên</TableCell>
                    <TableCell>Mô tả</TableCell>
                    <TableCell>Hành động</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {categories.map((category) => (
                    <TableRow key={category._id}>
                      <TableCell>{category.name}</TableCell>
                      <TableCell>{category.description}</TableCell>
                      <TableCell>
                        {hasPermission(["category:update"]) && (
                          <IconButton
                            onClick={() => handleClickOpen(category)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        )}
                        {hasPermission(["category:delete"]) && (
                          <IconButton
                            onClick={() => handleDelete(category._id)}
                            color="secondary"
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={totalCategories}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
            />
          </>
        )}

        <Dialog open={open} onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          handleClose();
        }
    }}>
          <DialogTitle>
            {currentCategory ? "Chỉnh sửa Danh mục" : "Thêm Danh mục"}
          </DialogTitle>
          <DialogContent>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                label="Tên Danh mục"
                name="name"
                value={form.name}
                onChange={handleChange}
                error={!!validationErrors.name}
                helperText={validationErrors.name}
              />
              <TextField
                margin="normal"
                fullWidth
                label="Mô tả"
                name="description"
                value={form.description}
                onChange={handleChange}
                error={!!validationErrors.description}
                helperText={validationErrors.description}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Hủy</Button>
            <Button onClick={handleSubmit}>
              {currentCategory ? "Cập nhật" : "Thêm"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default Categories;
